import { createPublicClient, getAddress, http } from 'viem';
import { mainnet } from 'viem/chains';

import { normalize } from 'viem/ens';

import { ENVS } from '@/constants';

import { WalletType } from '@/utils/wallet';
import { ISignInMessage } from '@/utils/wallet/types';

const getPublicClient = () => {
  const publicClient = createPublicClient({
    chain: mainnet,
    transport: http(ENVS.VITE_ALCHEMY_API_KEY)
  });
  return publicClient;
};

export const getEnsName = async (address: `0x${string}`): Promise<string | null> => {
  const publicClient = getPublicClient();
  return await publicClient.getEnsName({
    address
  });
};

export const getEnsAddress = async (name: string): Promise<string | null> => {
  const publicClient = getPublicClient();
  return await publicClient.getEnsAddress({
    name: normalize(name)
  });
};

export const getSignMessage = (params: {
  statement: string;
  domain?: string;
  address?: string;
  version?: string;
  nonce: string;
  issuedAt: string;
  chainId?: string | number;
  signType?: string;
  origin?: string;
}): string => {
  return JSON.stringify(params, null, '  ');
};

const isEvmAddressValidate = (address: string): boolean => {
  // 在以太坊网络中，有效的地址应以'0x'开头，总长度为42个字符
  return address.startsWith('0x') && address.length === 42;
};

export const walletValidators = {
  [WalletType.EVM]: isEvmAddressValidate,
  [WalletType.Coinbase]: isEvmAddressValidate,
  // 修复ts问题
  [WalletType.OKX]: isEvmAddressValidate,
  [WalletType.BITCOIN]: function (address: string): boolean {
    // Legacy（传统地址）：以“1”开头，长度为26-35个字符
    // Segwit（隔离见证地址）：以“3”或“bc1”开头，长度为27-34个字符
    // Bech32（比特币原生隔离见证地址）：以“bc1”开头，长度为42个字符
    // taproot address: 以“bc1p”开头，长度为62个字符
    return (
      address.startsWith('bc1') &&
      ((address.length >= 26 && address.length <= 35) || address.length === 42 || address.length === 62)
    );
  },
  [WalletType.SOLANA]: function (address: string): boolean {
    return !!address && (address.length === 44 || address.length === 43);
  },
  [WalletType.APTOS]: function (address: string): boolean {
    return !!address && address.startsWith('0x') && address.length === 66;
  },
  [WalletType.TON]: function (address: string): boolean {
    return address?.length === 48 && (address.startsWith('EQ') || address.startsWith('UQ'));
  },
  [WalletType.PARTICLE]: function (address: string): boolean {
    // no need to validate
    return !!address;
  },
  [WalletType.Cyber]: function (address: string): boolean {
    return !!address;
  },
  [WalletType.StarkNet]: function (address: string): boolean {
    return address.startsWith('0x') && (address.length === 65 || address.length === 66);
  },
  [WalletType.WALLETCONNECT]: isEvmAddressValidate,
  [WalletType.Cardano]: function (address: string): boolean {
    return !!address;
  }
};

export const getNetworkByAddress = (address: string): WalletType | undefined => {
  if (walletValidators[WalletType.EVM](address)) {
    return WalletType.EVM;
  }
  if (walletValidators[WalletType.BITCOIN](address)) {
    return WalletType.BITCOIN;
  }
  if (walletValidators[WalletType.TON](address)) {
    return WalletType.TON;
  }
  if (walletValidators[WalletType.SOLANA](address)) {
    return WalletType.SOLANA;
  }
  if (walletValidators[WalletType.APTOS](address)) {
    return WalletType.APTOS;
  }
  if (walletValidators[WalletType.StarkNet](address)) {
    return WalletType.StarkNet;
  }
  return undefined;
};

export const transformAddress = (address: string, walletType: WalletType): string => {
  if (walletType === WalletType.EVM && address) {
    return getAddress(address);
  }
  return address;
};

export const isSupportPasswordManager = (chainType: WalletType): boolean => {
  return ![WalletType.TON, WalletType.APTOS, WalletType.PARTICLE, WalletType.StarkNet, WalletType.Cardano].includes(
    chainType
  );
};

// EIP-4361 formated message, ready for EIP-191 signing.
export const get4361Message = ({
  domain,
  version,
  nonce,
  issuedAt,
  statement,
  chainId,
  uri,
  address
}: ISignInMessage): string => {
  const header = `${domain} wants you to sign in with your Ethereum account:`;
  const uriField = `URI: ${uri}`;
  let prefix = [header, address].join('\n');
  const versionField = `Version: ${version}`;

  // const addressField = `Address: ` + address;

  const chainField = `Chain ID: ` + chainId || '1';

  const nonceField = `Nonce: ${nonce}`;

  const suffixArray = [uriField, versionField, chainField, nonceField];

  suffixArray.push(`Issued At: ${issuedAt}`);

  const suffix = suffixArray.join('\n');
  prefix = [prefix, statement].join('\n\n');
  if (statement) {
    prefix += '\n';
  }
  return [prefix, suffix].join('\n');
};
